import { DataRow } from './DataRow';

import { usePolicyInsightContext } from './PolicyInsight';

import { formatCurrencyValue } from './policyInsightUtils';

export function TotalInvestedIntoPolicy() {
  const { insightData } = usePolicyInsightContext();

  const monthlyPremium = insightData?.monthlyPremium;
  const isAnnualBilled = insightData?.isAnnualBilled ?? false;
  const annualPremium = insightData?.annualPremium ?? null;
  const policyDuration = insightData?.policyDuration;
  const monthlyPremiumTotalCost = insightData?.monthlyPremiumTotalCost;
  const lumpSumPayment = insightData?.lumpSumPayment;
  const predictedTotalPremiumsPaid = insightData?.predictedTotalPremiumsPaid;
  const isLumpSumOnly = insightData?.isLumpSumOnly;

  const hasMonthlyPremium = monthlyPremium && monthlyPremium > 0;
  const hasLumpSum = lumpSumPayment && lumpSumPayment > 0;
  const hasBothPremiumAndLumpSum = hasMonthlyPremium && hasLumpSum;
  // Premium label and amount adapt to billing frequency. The total-cost
  // formula always stays in months × monthly because that's exactly how the
  // engine computes `monthlyPremiumTotalCost` — using years × annual would
  // double-round and the displayed multiplication would drift dollars from
  // the displayed total.
  const premiumLabel = isAnnualBilled ? 'Annual Premium' : 'Monthly Premium';
  const headlinePremiumValue = isAnnualBilled ? annualPremium : monthlyPremium;

  const getHelperText = () => {
    if (isLumpSumOnly) {
      return undefined;
    }
    if (hasBothPremiumAndLumpSum && monthlyPremiumTotalCost) {
      return `(${formatCurrencyValue(monthlyPremiumTotalCost)} + ${formatCurrencyValue(lumpSumPayment)})`;
    }
    if (hasMonthlyPremium && predictedTotalPremiumsPaid) {
      return `(${policyDuration} x ${formatCurrencyValue(monthlyPremium)})`;
    }
    return undefined;
  };

  if (isLumpSumOnly) {
    return (
      <section>
        <h2 className="mb-4 text-lg font-semibold text-gray-950">Total Invested Into Policy</h2>
        <div className="space-y-4 rounded-xl border border-gray-200 p-4">
          <DataRow
            highlight="red"
            label="Lump Sum Payment"
          >
            {formatCurrencyValue(lumpSumPayment)}
          </DataRow>
          <hr className="border-gray-200" />
          <p className="text-sm italic text-gray-700">
            This is a single lump sum payment into the policy.
          </p>
        </div>
      </section>
    );
  }

  const getMonthlyPremiumHelperText = () => {
    if (hasBothPremiumAndLumpSum) {
      return `(${policyDuration} x ${formatCurrencyValue(monthlyPremium)})`;
    }
    return undefined;
  };

  return (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-gray-950">Total Invested Into Policy</h2>
      <div className="space-y-4 rounded-xl border border-gray-200 p-4">
        <DataRow
          label={premiumLabel}
          helperText={
            isAnnualBilled && monthlyPremium
              ? `(≈ ${formatCurrencyValue(monthlyPremium)}/mo)`
              : undefined
          }
        >
          {formatCurrencyValue(headlinePremiumValue)}
        </DataRow>
        <hr className="border-gray-200" />
        <DataRow label="Policy Duration">{policyDuration}</DataRow>
        {hasMonthlyPremium && (
          <>
            <hr className="border-gray-200" />
            <DataRow
              label={
                isAnnualBilled
                  ? 'Predicted Total Annual Premiums'
                  : 'Predicted Total Monthly Premiums'
              }
              helperText={getMonthlyPremiumHelperText()}
            >
              {formatCurrencyValue(monthlyPremiumTotalCost ?? predictedTotalPremiumsPaid)}
            </DataRow>
          </>
        )}
        {hasLumpSum && (
          <>
            <hr className="border-gray-200" />
            <DataRow label="Lump Sum Payment">{formatCurrencyValue(lumpSumPayment)}</DataRow>
          </>
        )}
        <hr className="border-gray-200" />
        <DataRow
          highlight="red"
          label="Predicted Total Premiums Paid"
          helperText={getHelperText()}
        >
          {formatCurrencyValue(predictedTotalPremiumsPaid)}
        </DataRow>
        <hr className="border-gray-200" />
        <p className="text-sm italic text-gray-700">
          This is the predicted total amount you'll pay in premiums before your policy activates.
        </p>
      </div>
    </section>
  );
}
